'use client';

import { useTranslations } from 'next-intl';
import { ADDED_TO_BASKET_MODAL_ID, buildRoute, NavRoute } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { useModalActions } from '@headless-workspace/core-ui';
import {
    GlowBasketProductPlot,
    GlowButton,
    GlowClickable,
    GlowIcon,
    GlowSkeleton,
    GlowTitle,
    GlowTypography,
    Icons,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { StringHelpers } from '@headless-workspace/utils';
import {
    ProductCarouselSkeleton,
    ProductDetailCarousel,
    useBasketModalRecommendedProducts,
    useClientData,
    useNavigation,
    VATLegalMention,
} from '../../../../client';
import { BasketProductValue, VipBrandsValue } from '../../../../server';

type ProductAddToBasketModalProps = PropsWithLocale & {
    product: BasketProductValue;
    vipBrands: VipBrandsValue;
};

export const ProductAddToBasketModal: Component<ProductAddToBasketModalProps> = ({ product, locale, vipBrands }) => {
    const { close } = useModalActions();

    const { getClientFlags } = useClientData();
    const isPriceLegalMentionEnabled = getClientFlags('isPriceExceptionLegalMentionsDisplayed');

    const t = useTranslations('Discover.Pdp.information.basket.modal');

    const navigation = useNavigation();

    const handleRedirect = (route: NavRoute) => () => {
        navigation.push(buildRoute(locale, route));
    };

    const { isLoading: isRecommendedProductsLoading, result: recommendedProductsResult } =
        useBasketModalRecommendedProducts(product.productId);

    const recommendedProducts = unwrapResultSuccess(recommendedProductsResult);

    const closeModal = () => {
        close(ADDED_TO_BASKET_MODAL_ID, true);
    };

    return (
        <section className={'flex flex-col bg-background-l0 w-full rounded-0.5 p-1 gap-1.5'}>
            <div className={'flex flex-row justify-between'}>
                <div className={'flex flex-row gap-1'}>
                    <GlowIcon Icon={Icons.Check} type={'large'} />
                    <GlowTypography
                        fontSize={'title4'}
                        TagName={'h3'}
                        fontWeight={'bold'}
                        innerHTML={t('label.title')}
                    />
                </div>
                <GlowClickable
                    content={{ icon: <GlowIcon Icon={Icons.Cross} type={'large'} /> }}
                    onClick={closeModal}
                />
            </div>
            <div>
                <div className={'flex flex-col justify-center'}>
                    <GlowBasketProductPlot
                        {...product}
                        discountPercentageLabel={
                            product.discountPercentage
                                ? t('label.discountPercentage', {
                                      discountPercentage: product.discountPercentage,
                                  })
                                : undefined
                        }
                        price={StringHelpers.formatPriceWithConditions(
                            product.price,
                            product.currency,
                            locale,
                            isPriceLegalMentionEnabled,
                        )}
                        priceBeforeDiscount={StringHelpers.formatPrice({
                            price: product.priceBeforeDiscount,
                            currency: product.currency,
                            locale,
                        })}
                        VATLegalMentionContent={isPriceLegalMentionEnabled && <VATLegalMention isClickable={false} />}
                    />
                    <div className={'flex flex-col tablet:flex-row justify-center pt-1 pb-2.5 gap-0.5'}>
                        <GlowButton
                            onClick={handleRedirect('payment')}
                            className={'flex-1'}
                            label={t('label.payment')}
                            size={'large'}
                            variant={'primary'}
                        />
                        <GlowButton
                            onClick={handleRedirect('basket')}
                            className={'flex-1'}
                            label={t('label.seeBasket')}
                            size={'large'}
                            variant={'secondary'}
                        />
                    </div>
                </div>
                {isRecommendedProductsLoading ? (
                    <div className={'flex flex-col gap-1.5 tablet:gap-2'}>
                        <GlowSkeleton height={'2rem'} width={'15rem'} />
                        <ProductCarouselSkeleton navigationButtonClassName={'hidden tablet:block'} />
                    </div>
                ) : (
                    recommendedProducts && (
                        <div className={'flex flex-col gap-1.5 tablet:gap-2'}>
                            <GlowTitle fontSize={'title3'} TagName={'h3'} innerHTML={t('carousel.label.title')} />
                            <ProductDetailCarousel
                                products={recommendedProducts.products}
                                wishlistBtnAriaLabel={t('carousel.ariaLabel.wishlist')}
                                locale={locale}
                                navigationButtonClassName={'hidden tablet:block'}
                                vipBrands={vipBrands}
                                enablePriceExceptionLegalMentions={isPriceLegalMentionEnabled}
                            />
                        </div>
                    )
                )}
            </div>
        </section>
    );
};
